import { useEffect, useRef, useState } from 'react';

/**
 * Tekst bijtekenen in plaats van hem laten verspringen.
 *
 * De server schrijft zijn tussenstand per 350 tekens weg en dit scherm vraagt
 * hem elke twee seconden op. Zonder iets ertussen komt de tekst dus binnen in
 * sprongen van een paar honderd tekens per keer, en dat leest niet als tekst die
 * wordt geschreven maar als een blok dat verspringt. Hier tussen die twee zit
 * het bijtekenen: wat er binnen is wordt over de volgende seconden uitgedeeld.
 */

/**
 * Wat er te zien moet zijn: het vorige beeld, aangevuld met wat er in deze tik
 * bij mag.
 *
 * De hele truc zit in de eerste regel. Bij het samenvatten groeit de tekst aan
 * het eind, dus is `doel` een langere versie van wat er al staat en kun je
 * bijtekenen. Bij het uitschrijven levert de server alleen het staartstuk van
 * een tekst van 24.000 tekens, dus schuift het venster en klopt het begin niet
 * meer. Daar zou bijtekenen tekens neerzetten die er niet staan, dus springt het
 * mee. Dezelfde regel dekt ook de fasewissel, die met een lege tekst begint.
 */
export function volgende(getoond: string, doel: string, ruimte: number): string {
  if (!doel.startsWith(getoond)) return doel;
  if (getoond.length === doel.length) return getoond;
  return doel.slice(0, getoond.length + Math.max(1, Math.floor(ruimte)));
}

/** Eén tik per 40 milliseconden: vijfentwintig keer per seconde is vloeiend
 *  genoeg voor tekst en ruim minder werk dan meelopen met het beeldscherm. */
const TIK_MS = 40;

/**
 * De tekst zoals hij nu getoond moet worden.
 *
 * `tekensPerSeconde` is de bodem, niet de snelheid. Komt er meer binnen dan dat,
 * dan wordt de achterstand in ongeveer twee seconden weggewerkt; anders loopt
 * het scherm steeds verder achter op wat er werkelijk al is geschreven, en aan
 * het eind zou je nog minuten zitten wachten op tekst die er al lang staat.
 */
export function useGeleidelijk(doel: string, tekensPerSeconde = 180): string {
  const [getoond, setGetoond] = useState('');
  // Via een ref, zodat het interval niet bij elke nieuwe tekst opnieuw wordt
  // opgezet; dan begint de tik telkens van voren af aan.
  const doelRef = useRef(doel);
  doelRef.current = doel;

  useEffect(() => {
    const timer = setInterval(() => {
      setGetoond((huidig) => {
        const d = doelRef.current;
        // Dezelfde waarde teruggeven betekent geen hertekening, dus stilstand
        // kost niets.
        if (huidig === d) return huidig;
        const achterstand = d.length - huidig.length;
        const snelheid = Math.max(tekensPerSeconde, achterstand / 2);
        return volgende(huidig, d, (snelheid * TIK_MS) / 1000);
      });
    }, TIK_MS);
    return () => clearInterval(timer);
  }, [tekensPerSeconde]);

  return getoond;
}
